"use client";

import { useRouter } from "next/navigation";
import { useCallback, useEffect, useId, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { Alert } from "../../bausteine/alert";
import { Badge, type BadgeTon } from "../../bausteine/badge";
import { Auswahl } from "../../bausteine/formular/auswahl";
import { Suchfeld } from "../../bausteine/formular/textfeld";
import { Knopf } from "../../bausteine/knopf";
import { LeerZustand } from "../../bausteine/leerzustand";
import { useEingehaengt } from "../../bausteine/modal/kleines-modal";
import { useModalVerhalten } from "../../bausteine/modal/modal-verhalten";
import { Schnellfilter } from "../../bausteine/schnellfilter";
import {
  autoAktualisierenErlaubt,
  extrahiereLinks,
  groesseLabel,
  KANAELE,
  kanalLabel,
  mailSrcdoc,
  oeffneImHauptfenster,
  personAnzeigeName,
  pillenLabel,
  pillenZahl,
  POSTFACH_AUTO_MS,
  POSTFACH_FENSTER_MERKMALE,
  POSTFACH_FENSTER_NAME,
  POSTFACH_FENSTER_PFAD,
  POSTFACH_POPUP_GEBLOCKT_SATZ,
  POSTFACH_FEHLSCHLAG_DETAIL,
  POSTFACH_FEHLSCHLAG_LEEREN,
  POSTFACH_FEHLSCHLAG_LISTE,
  POSTFACH_FEHLSCHLAG_PERSONEN,
  satzFuerAntwort,
  statusLabel,
  zeitKurz,
  zeitLang,
  zeitraumVon,
  ZEITRAEUME,
  type PostfachDetailDaten,
  type PostfachEintrag,
  type PostfachKanal,
  type PostfachPerson,
  type PostfachPilleProps,
  type ZeitraumId,
} from "./postfach-logik";
import {
  AbkoppelnSymbol,
  AktualisierenSymbol,
  ExternSymbol,
  KanalSymbol,
  LinkSymbol,
  PapierkorbSymbol,
  PostfachSymbol,
  SchliessenX,
  ZurueckSymbol,
} from "./postfach-symbole";

/**
 * Pille „Postfach“ rechts neben der DEV-Pille und das virtuelle Postfach als großes Modal (Musterkatalog M-067,
 * Design-Guide `dev-postfach.md`; Hülle M-008 D1, Liste|Detail D4). Der Baustein prüft nichts: ob er ausgeliefert wird,
 * welche Personen wählbar sind und was gelesen oder geleert werden darf, entscheidet der Server (die hereingereichten
 * Actions prüfen selbst, `darfPostfachLesen`, r109/r110). Zähler nur für die gezeigte Person (r177).
 */

// Abweichung AW-083 (1): eigener Pillen-Ton Teal (Betreiber 13.10.2026, PQ1 a), unterscheidbar von DEV (Primär) und STAGE (Gelb).
const PILLE =
  "inline-flex items-center gap-x-1.5 py-1 ps-2 pe-1.5 rounded-full text-xs font-medium bg-teal-100 text-teal-800 hover:bg-teal-200 focus:outline-hidden focus:ring-2 focus:ring-primary";
// Idee I2: nach einer Ablage hebt sich die Pille kurz hervor (statt Toast); bei reduzierter Bewegung ohne Animation.
const PILLE_HERVOR =
  "inline-flex items-center gap-x-1.5 py-1 ps-2 pe-1.5 rounded-full text-xs font-medium bg-teal-100 text-teal-800 hover:bg-teal-200 focus:outline-hidden focus:ring-2 focus:ring-primary ring-2 ring-teal-600 animate-pulse motion-reduce:animate-none";
const ZAEHLER = "inline-flex items-center justify-center min-w-5 h-5 px-1 rounded-full text-xs font-semibold tabular-nums bg-teal-800 text-teal-50";

// Spalten des Modals: feste, vollständige Ketten (R8). Unter `lg` ist immer nur Liste (mit Filtern) ODER Detail sichtbar.
const SPALTE_FILTER = "shrink-0 lg:w-72 border-b lg:border-b-0 lg:border-e border-overlay-header p-4 space-y-4 overflow-y-auto";
const SPALTE_FILTER_MOBIL_AUS = "max-lg:hidden shrink-0 lg:w-72 border-b lg:border-b-0 lg:border-e border-overlay-header p-4 space-y-4 overflow-y-auto";
const SPALTE_LISTE = "lg:w-96 shrink-0 grow lg:grow-0 min-h-0 flex flex-col lg:border-e border-overlay-header";
const SPALTE_LISTE_MOBIL_AUS = "max-lg:hidden lg:w-96 shrink-0 grow lg:grow-0 min-h-0 flex flex-col lg:border-e border-overlay-header";
const SPALTE_DETAIL = "grow min-w-0 min-h-0 flex flex-col";
const SPALTE_DETAIL_MOBIL_AUS = "max-lg:hidden grow min-w-0 min-h-0 flex flex-col";
const ZEILE = "w-full text-start px-3 py-3 flex gap-x-2 hover:bg-muted-hover focus:outline-hidden focus:bg-muted-focus";
const ZEILE_GEWAEHLT = "w-full text-start px-3 py-3 flex gap-x-2 hover:bg-muted-hover focus:outline-hidden focus:bg-muted-focus bg-muted";
const BETREFF = "mt-1 block truncate text-sm text-foreground";
const BETREFF_UNGELESEN = "mt-1 block truncate text-sm text-foreground font-semibold";

const HERVOR_MS = 3000;
const SUCHE_VERZOEGERUNG_MS = 350;

export function PostfachPille(p: PostfachPilleProps) {
  const [offen, setzeOffen] = useState(false);
  const [hervor, setzeHervor] = useState(false);
  const vorher = useRef<number | null>(p.ungelesen);
  const router = useRouter();

  // I2: steigt die Zahl (neue Ablage), hebt sich die Pille kurz hervor; der Server liefert die Zahl bei jedem Seitenwechsel neu.
  useEffect(() => {
    const alt = vorher.current;
    vorher.current = p.ungelesen;
    if (alt !== null && p.ungelesen !== null && p.ungelesen > alt) {
      setzeHervor(true);
      const t = setTimeout(() => setzeHervor(false), HERVOR_MS);
      return () => clearTimeout(t);
    }
  }, [p.ungelesen]);

  const schliessen = useCallback(() => {
    setzeOffen(false);
    router.refresh(); // Zahl der Pille nach dem Lesen und Leeren neu vom Server
  }, [router]);

  const zahl = pillenZahl(p.ungelesen);
  return (
    <>
      {/* Preline: component/badge/badge-soft-color-variants (Abzeichen-Optik, bedienbar; Ton Teal) */}
      <button
        type="button"
        className={hervor ? PILLE_HERVOR : PILLE}
        aria-haspopup="dialog"
        aria-expanded={offen}
        aria-label={pillenLabel(p.ungelesen)}
        onClick={() => setzeOffen(true)}
      >
        <PostfachSymbol klasse="shrink-0 size-3.5" />
        <span className="hidden sm:inline">Postfach</span>
        {zahl && <span className={ZAEHLER}>{zahl}</span>}
      </button>
      {offen && <PostfachModal {...p} beimSchliessen={schliessen} />}
    </>
  );
}

const KANAL_TON: Record<PostfachKanal, BadgeTon> = {
  mail: "primaer",
  kalender: "erfolg",
  teams: "neutral",
  push: "warnung",
  glocke: "gedaempft",
  webhook: "gedaempft",
};

function KanalBadge({ kanal }: { kanal: PostfachKanal }) {
  return (
    <Badge ton={KANAL_TON[kanal]}>
      <KanalSymbol kanal={kanal} />
      {kanalLabel(kanal)}
    </Badge>
  );
}

type ListeZustand = { eintraege: PostfachEintrag[]; ungelesen: number | null; zielUserId: string };

/** Portal erst nach dem Einhängen; der Inhalt (mit dem Modal-Verhalten) entsteht erst dann, damit die Hülle für Esc, Fokusfalle und Fokus-Rückgabe da ist. */
function PostfachModal(p: PostfachPilleProps & { beimSchliessen: () => void }) {
  const eingehaengt = useEingehaengt();
  if (!eingehaengt) return null;
  return createPortal(<PostfachInhalt {...p} />, document.body);
}

function PostfachInhalt({ beimSchliessen, ...p }: PostfachPilleProps & { beimSchliessen: () => void }) {
  const huelle = useRef<HTMLDivElement>(null);
  const id = useId();
  const [geblockt, setzeGeblockt] = useState(false);
  useModalVerhalten({ offen: true, huelle, beimSchliessen });

  // W119 a: das Postfach in ein eigenes Fenster abkoppeln. Benannt: ein erneuter Klick (auch aus einem anderen Tab) fokussiert dasselbe
  // Fenster. OHNE `noopener`, denn das Fenster braucht `window.opener`, um Portal-Links im Hauptfenster zu öffnen; sicher, weil
  // die Zielseite eine eigene Seite derselben Origin ist (kein fremder Inhalt kann dort laufen; die Mail-Darstellung ist
  // `sandbox=""`, externe Links tragen `noopener noreferrer`). Blockiert der Browser das Fenster, bleibt das Modal offen und
  // der Satz bietet die Seite im neuen Tab an (kein Toast).
  function koppleAb() {
    const fenster = window.open(POSTFACH_FENSTER_PFAD, POSTFACH_FENSTER_NAME, POSTFACH_FENSTER_MERKMALE);
    if (!fenster) {
      setzeGeblockt(true);
      return;
    }
    fenster.focus();
    beimSchliessen();
  }

  return (
    <>
      {/* Preline: productivity/work-management/projects.html#project-details (Hülle M-008 D1) */}
      {/* Abweichung AW-083 (3) bis (10): drei Spalten statt Hauptteil | Seitenteil, Kanal-Schnellfilter, kein „Alle gelesen“, Zeitraum und Person als Auswahlfeld, Hinweis über der Liste, Mail ohne Links, Leeren mit Rückfrage im Fuß. */}
      <div className="fixed inset-0 z-70 bg-foreground/50 backdrop-blur-md" aria-hidden="true" onClick={beimSchliessen} />
      <div
        ref={huelle}
        className="fixed top-0 inset-s-0 size-full z-80 overflow-hidden pointer-events-none"
        role="dialog"
        aria-modal="true"
        aria-labelledby={`${id}-titel`}
        tabIndex={-1}
      >
        <div className="mt-0 lg:mt-7 opacity-100 lg:max-w-6xl 2xl:max-w-7xl lg:w-full m-0 lg:m-3 lg:mx-auto h-full lg:h-[calc(100%-56px)] lg:min-h-[calc(100%-56px)] flex items-center">
          <div className="size-full max-h-full flex flex-col bg-overlay border border-overlay-line lg:rounded-xl overflow-hidden pointer-events-auto shadow-xl">
            {/* Kopf */}
            <div className="shrink-0 border-b border-overlay-header py-2.5 ps-4 pe-2 sm:ps-6 flex items-center justify-between gap-x-2">
              <PostfachTitel idTitel={`${id}-titel`} fokus />
              <div className="shrink-0 flex items-center gap-x-1">
                <Knopf variante="ghost" groesse="sm" symbol={<AbkoppelnSymbol />} onClick={koppleAb} titel="In eigenem Fenster öffnen">
                  Abkoppeln
                </Knopf>
                <Knopf variante="ghost" nurSymbol groesse="sm" label="Schließen (Esc)" symbol={<SchliessenX />} onClick={beimSchliessen} />
              </div>
            </div>
            {geblockt && (
              <div className="shrink-0 border-b border-overlay-header px-4 sm:px-6 py-2">
                <Alert ton="warnung">
                  {POSTFACH_POPUP_GEBLOCKT_SATZ}{" "}
                  <Knopf variante="text" href={POSTFACH_FENSTER_PFAD} extern symbol={<ExternSymbol />} ariaLabel="Im neuen Tab öffnen (öffnet in neuem Tab)">
                    Im neuen Tab öffnen
                  </Knopf>
                </Alert>
              </div>
            )}
            <PostfachArbeitsflaeche {...p} fenster={false} />
          </div>
        </div>
      </div>
    </>
  );
}

function PostfachTitel({ idTitel, fokus }: { idTitel: string; fokus: boolean }) {
  return (
    <div className="min-w-0 flex items-start gap-x-2">
      <span className="mt-1 text-foreground">
        <PostfachSymbol />
      </span>
      <div className="min-w-0">
        <h2 id={idTitel} className="text-lg font-semibold text-foreground" tabIndex={fokus ? -1 : undefined} data-modal-fokus={fokus ? true : undefined}>
          Virtuelles Postfach
        </h2>
        <p className="text-xs text-muted-foreground-1">Alles, was das System versendet hätte</p>
      </div>
    </div>
  );
}

/**
 * Das abgekoppelte Postfach als eigene Seite (`/dev/postfach`, W119 a): dieselbe Arbeitsfläche wie das Modal (Filter | Liste |
 * Detail), nur ohne Hülle. Der Baustein prüft nichts; ob die Seite ausgeliefert wird, entscheidet die Seite mit derselben
 * Prüfung wie die Pille (`ladePostfachPille`), sonst `notFound()`.
 */
export function PostfachFenster(p: PostfachPilleProps) {
  const id = useId();
  return (
    <main className="h-dvh flex flex-col bg-background text-foreground">
      {/* Preline: productivity/work-management/projects.html#project-details (Hülle M-008 D1, als Vollseite ohne Overlay; Abweichung AW-083 (13)) */}
      <div className="shrink-0 border-b border-line-2 py-2.5 px-4 sm:px-6 flex items-center justify-between gap-x-2">
        <PostfachTitel idTitel={`${id}-titel`} fokus={false} />
      </div>
      <PostfachArbeitsflaeche {...p} fenster />
    </main>
  );
}

/**
 * Gemeinsamer Inhalt von Modal und Fenster (Filter | Liste | Detail | Fuß); `fenster` schaltet nur Auto-Aktualisierung, den
 * Aktualisieren-Knopf und das Öffnen der Portal-Links im Hauptfenster zu.
 */
function PostfachArbeitsflaeche({ istStage, aufbewahrungTage, aktionen: aktionenProp, fenster }: PostfachPilleProps & { fenster: boolean }) {
  // Die Actions kommen bei jedem Server-Render als neues Objekt; die Abfragen hängen nicht an ihrer Identität (Stand beim Öffnen).
  const [aktionen] = useState(aktionenProp);

  const [personen, setzePersonen] = useState<PostfachPerson[] | null>(null);
  const [personenSatz, setzePersonenSatz] = useState<string | null>(null);
  const [ziel, setzeZiel] = useState<string | undefined>(undefined);
  const [kanal, setzeKanal] = useState<PostfachKanal | "alle">("alle");
  const [zeitraum, setzeZeitraum] = useState<ZeitraumId>("alle");
  const [suche, setzeSuche] = useState("");
  const [sucheWirksam, setzeSucheWirksam] = useState("");
  const [liste, setzeListe] = useState<ListeZustand | null>(null);
  const [listeSatz, setzeListeSatz] = useState<string | null>(null);
  const [laedt, setzeLaedt] = useState(true);
  const [detailId, setzeDetailId] = useState<string | null>(null);
  const [detail, setzeDetail] = useState<PostfachDetailDaten | null>(null);
  const [detailSatz, setzeDetailSatz] = useState<string | null>(null);
  const [leerenSatz, setzeLeerenSatz] = useState<string | null>(null);
  const [bestaetige, setzeBestaetige] = useState<"eigenes" | "alle" | null>(null);
  const [arbeitet, setzeArbeitet] = useState(false);
  const anfrage = useRef(0);
  const detailAnfrage = useRef(0);

  // Personen beim Öffnen und bei Zeitraumwechsel (die Action filtert; Admins kommen nie vor, r110); die Zahl hinter dem Namen
  // ist die Gesamtzahl im Zeitraum, unabhängig vom Kanalfilter (Betreiber W118 a).
  const personenAnfrage = useRef(0);
  const ladePersonen = useCallback(() => {
    const nr = ++personenAnfrage.current;
    aktionen
      .personen(undefined, zeitraumVon(zeitraum, new Date()))
      .then((a) => {
        if (nr !== personenAnfrage.current) return;
        if (a.ok) {
          setzePersonen(a.personen);
          setzePersonenSatz(null);
        } else setzePersonenSatz(satzFuerAntwort(a, POSTFACH_FEHLSCHLAG_PERSONEN));
      })
      .catch(() => {
        if (nr === personenAnfrage.current) setzePersonenSatz(POSTFACH_FEHLSCHLAG_PERSONEN);
      });
  }, [aktionen, zeitraum]);

  useEffect(() => {
    ladePersonen();
  }, [ladePersonen]);

  // Suche mit Verzögerung (jede Abfrage eines fremden Postfachs wird serverseitig protokolliert).
  useEffect(() => {
    const t = setTimeout(() => setzeSucheWirksam(suche.trim()), SUCHE_VERZOEGERUNG_MS);
    return () => clearTimeout(t);
  }, [suche]);

  const ladeListe = useCallback(async () => {
    const nr = ++anfrage.current;
    setzeLaedt(true);
    const von = zeitraumVon(zeitraum, new Date());
    try {
      const a = await aktionen.liste({
        ...(ziel ? { zielUserId: ziel } : {}),
        ...(kanal !== "alle" ? { kanal } : {}),
        ...(sucheWirksam ? { suche: sucheWirksam } : {}),
        ...(von ? { von } : {}),
        limit: 100,
      });
      if (nr !== anfrage.current) return;
      if (a.ok) {
        setzeListe({ eintraege: a.eintraege, ungelesen: a.ungelesen, zielUserId: a.zielUserId });
        setzeListeSatz(null);
      } else {
        setzeListe(null);
        setzeListeSatz(satzFuerAntwort(a, POSTFACH_FEHLSCHLAG_LISTE));
      }
    } catch {
      if (nr !== anfrage.current) return;
      setzeListe(null);
      setzeListeSatz(POSTFACH_FEHLSCHLAG_LISTE);
    } finally {
      if (nr === anfrage.current) setzeLaedt(false);
    }
  }, [aktionen, ziel, kanal, sucheWirksam, zeitraum]);

  useEffect(() => {
    void ladeListe();
  }, [ladeListe]);

  function waehleZiel(neu: string) {
    setzeZiel(neu || undefined);
    setzeDetailId(null);
    setzeDetail(null);
    setzeDetailSatz(null);
    setzeBestaetige(null);
    setzeLeerenSatz(null);
  }

  async function oeffneDetail(eintragId: string) {
    const nr = ++detailAnfrage.current;
    setzeDetailId(eintragId);
    setzeDetail(null);
    setzeDetailSatz(null);
    try {
      const a = await aktionen.detail(eintragId);
      if (nr !== detailAnfrage.current) return;
      if (a.ok) {
        setzeDetail(a.detail);
        // Das Lesen setzt der Server nur im eigenen Postfach; die Liste folgt dem, was er meldet (`gelesen` im Detail).
        if (a.detail.gelesen === true) {
          setzeListe((l) => {
            if (!l) return l;
            const war = l.eintraege.find((e) => e.id === eintragId)?.gelesen === false;
            return {
              ...l,
              ungelesen: war && l.ungelesen !== null ? Math.max(0, l.ungelesen - 1) : l.ungelesen,
              eintraege: l.eintraege.map((e) => (e.id === eintragId ? { ...e, gelesen: true } : e)),
            };
          });
        }
      } else {
        setzeDetailSatz(satzFuerAntwort(a, POSTFACH_FEHLSCHLAG_DETAIL));
      }
    } catch {
      if (nr === detailAnfrage.current) setzeDetailSatz(POSTFACH_FEHLSCHLAG_DETAIL);
    }
  }

  async function leere(art: "eigenes" | "alle") {
    setzeArbeitet(true);
    setzeLeerenSatz(null);
    try {
      const a = art === "alle" ? await aktionen.leereAlle?.() : await aktionen.leereEigenes(liste?.zielUserId);
      if (!a) return;
      if (a.ok) {
        setzeBestaetige(null);
        setzeDetailId(null);
        setzeDetail(null);
        await ladeListe();
      } else {
        // Offen bleiben bei Ablehnung und Fehlschlag (r98): der Satz steht an der Stelle, an der man geklickt hat.
        setzeLeerenSatz(satzFuerAntwort(a, POSTFACH_FEHLSCHLAG_LEEREN));
      }
    } catch {
      setzeLeerenSatz(POSTFACH_FEHLSCHLAG_LEEREN);
    } finally {
      setzeArbeitet(false);
    }
  }

  const eigeneId = personen?.find((p) => p.eigen)?.id;
  const zielAnzeige = liste?.zielUserId ?? ziel ?? eigeneId ?? "";
  const istEigen = personen ? personen.some((p) => p.eigen && p.id === (liste?.zielUserId ?? ziel ?? p.id)) : true;
  const anzahl = liste?.eintraege.length ?? 0;
  const gesamtUngelesen = liste?.ungelesen;
  const personenOptionen = personen
    ? personen.map((p) => ({ wert: p.id, label: personAnzeigeName(p) }))
    : [{ wert: zielAnzeige, label: "Mein Postfach" }];

  // Auto-Aktualisierung (nur im abgekoppelten Fenster, W119 a): alle 15 s die Liste der gezeigten Person, aber nur im EIGENEN
  // Postfach und nur bei sichtbarem Tab. Ein fremdes Postfach wird je Abruf serverseitig protokolliert (fremd_eingesehen): dort
  // nur per Knopf. Der Takt zählt nichts und merkt sich nichts (r177).
  useEffect(() => {
    if (!fenster || !istEigen) return;
    const t = setInterval(() => {
      if (autoAktualisierenErlaubt(istEigen, document.visibilityState)) void ladeListe();
    }, POSTFACH_AUTO_MS);
    return () => clearInterval(t);
  }, [fenster, istEigen, ladeListe]);

  function aktualisiere() {
    ladePersonen();
    void ladeListe();
  }

  return (
    <>
      {/* Preline: communication/communication-sub-chat-inbox/communication-cat-inbox-layouts/shared-inbox-workspace-with-thread-list-and-reply-pane (Liste | Detail, M-008 D4) */}
      {/* Abweichung AW-083 (3) bis (10), (13): drei Spalten statt Hauptteil | Seitenteil, Kanal-Schnellfilter, kein „Alle gelesen“, Zeitraum und Person als Auswahlfeld, Hinweis über der Liste, Mail ohne Links, Leeren mit Rückfrage im Fuß; abgekoppelt als Vollseite. */}
      {/* Körper: Filter | Liste | Detail (ab lg nebeneinander; darunter Liste, dann Detail als Vollfläche) */}
            <div className="grow min-h-0 flex flex-col lg:flex-row overflow-hidden">
              <div className={detailId !== null ? SPALTE_FILTER_MOBIL_AUS : SPALTE_FILTER}>
                <Auswahl
                  label="Postfach von"
                  groesse="kompakt"
                  value={zielAnzeige}
                  onChange={(e) => waehleZiel(e.target.value)}
                  optionen={personenOptionen}
                  disabled={!personen}
                  hinweissatz={personenSatz ?? undefined}
                />
                <div className="space-y-1.5">
                  <p className="text-sm font-medium text-foreground">Kanal</p>
                  <Schnellfilter<PostfachKanal | "alle">
                    titel="Kanal"
                    optionen={[{ wert: "alle", label: "Alle" }, ...KANAELE.map((k) => ({ wert: k.id, label: k.label }))]}
                    wert={kanal}
                    leerWert="alle"
                    onWahl={(neu) => {
                      setzeKanal(neu);
                      setzeDetailId(null);
                    }}
                  />
                </div>
                <Auswahl
                  label="Zeitraum"
                  groesse="kompakt"
                  value={zeitraum}
                  onChange={(e) => setzeZeitraum(e.target.value as ZeitraumId)}
                  optionen={ZEITRAEUME.map((z) => ({ wert: z.id, label: z.label }))}
                />
              </div>

              {/* Liste */}
              <div className={detailId !== null ? SPALTE_LISTE_MOBIL_AUS : SPALTE_LISTE}>
                <div className="shrink-0 p-3 border-b border-overlay-header space-y-3">
                  <Alert ton="warnung">
                    Nur {istStage ? "Stage" : "Dev"}: Diese Nachrichten wurden <strong>nicht versendet</strong>. Sie werden nach {aufbewahrungTage} Tagen gelöscht.
                  </Alert>
                  <Suchfeld label="Postfach durchsuchen" placeholder="Betreff, Text, Absender …" value={suche} onChange={(e) => setzeSuche(e.target.value)} />
                </div>
                <div className="grow min-h-0 overflow-y-auto" aria-busy={laedt}>
                  {listeSatz ? (
                    <div className="p-3">
                      <Alert ton="warnung">{listeSatz}</Alert>
                    </div>
                  ) : laedt && !liste ? (
                    <p className="p-4 text-sm text-muted-foreground-1" role="status">
                      Lade Nachrichten …
                    </p>
                  ) : liste && liste.eintraege.length === 0 ? (
                    <LeerZustand titel="Noch keine Nachrichten." text="Sobald das System etwas versendet hätte, steht es hier." symbol={<PostfachSymbol />} />
                  ) : (
                    <ul className="divide-y divide-line-2" aria-label="Nachrichten">
                      {liste?.eintraege.map((e) => (
                        <li key={e.id}>
                          <EintragZeile eintrag={e} gewaehlt={e.id === detailId} beiWahl={() => void oeffneDetail(e.id)} />
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              </div>

              {/* Detail */}
              <div className={detailId === null ? SPALTE_DETAIL_MOBIL_AUS : SPALTE_DETAIL}>
                <div className="lg:hidden shrink-0 p-2 border-b border-overlay-header">
                  <Knopf variante="ghost" groesse="sm" symbol={<ZurueckSymbol />} onClick={() => setzeDetailId(null)}>
                    Zur Liste
                  </Knopf>
                </div>
                <div className="grow min-h-0 overflow-y-auto p-4 sm:p-6">
                  {detailSatz ? (
                    <Alert ton="warnung">{detailSatz}</Alert>
                  ) : detailId === null ? (
                    <LeerZustand titel="Keine Nachricht gewählt." text="Wähle links eine Nachricht, um sie zu lesen." symbol={<PostfachSymbol />} />
                  ) : !detail ? (
                    <p className="text-sm text-muted-foreground-1" role="status">
                      Lade Nachricht …
                    </p>
                  ) : (
                    <DetailAnsicht detail={detail} fenster={fenster} />
                  )}
                </div>
              </div>
            </div>

            {/* Fuß */}
            <div className="shrink-0 border-t border-overlay-header px-4 sm:px-6 py-2 space-y-2">
              {leerenSatz && <Alert ton="warnung">{leerenSatz}</Alert>}
              <div className="flex flex-wrap items-center justify-between gap-2 text-xs text-muted-foreground-1">
                <span role="status">
                  {liste ? `${anzahl} ${anzahl === 1 ? "Nachricht" : "Nachrichten"}` : ""}
                  {typeof gesamtUngelesen === "number" ? ` · ${gesamtUngelesen} ungelesen` : ""}
                </span>
                {fenster && (
                  <span className="flex flex-wrap items-center gap-2">
                    <span role="note">{istEigen ? "Aktualisiert sich alle 15 Sekunden." : "Fremdes Postfach: nur per Knopf."}</span>
                    <Knopf variante="ghost" groesse="xs" symbol={<AktualisierenSymbol />} onClick={aktualisiere}>
                      Aktualisieren
                    </Knopf>
                  </span>
                )}
                {bestaetige ? (
                  <span className="flex flex-wrap items-center gap-2">
                    <span className="text-foreground">{bestaetige === "alle" ? "Wirklich alle Postfächer leeren?" : "Dieses Postfach wirklich leeren?"}</span>
                    <Knopf variante="gefahr" groesse="xs" laedt={arbeitet} onClick={() => void leere(bestaetige)}>
                      Ja, leeren
                    </Knopf>
                    <Knopf variante="sekundaer" groesse="xs" disabled={arbeitet} onClick={() => setzeBestaetige(null)}>
                      Abbrechen
                    </Knopf>
                  </span>
                ) : (
                  <span className="flex flex-wrap items-center gap-2">
                    {/* Anbieten = Erlauben: „Postfach leeren“ nur im eigenen Postfach, „Alle leeren“ nur, wo die Action es der Person erlaubt. */}
                    {istEigen && liste && (
                      <Knopf variante="ghost" groesse="xs" symbol={<PapierkorbSymbol />} onClick={() => setzeBestaetige("eigenes")}>
                        Postfach leeren
                      </Knopf>
                    )}
                    {aktionen.leereAlle && (
                      <Knopf variante="ghost" groesse="xs" symbol={<PapierkorbSymbol />} onClick={() => setzeBestaetige("alle")}>
                        Alle Postfächer leeren
                      </Knopf>
                    )}
                  </span>
                )}
              </div>
            </div>
    </>
  );
}

function EintragZeile({ eintrag, gewaehlt, beiWahl }: { eintrag: PostfachEintrag; gewaehlt: boolean; beiWahl: () => void }) {
  const ungelesen = eintrag.gelesen === false;
  return (
    <button
      type="button"
      onClick={beiWahl}
      aria-current={gewaehlt ? "true" : undefined}
      className={gewaehlt ? ZEILE_GEWAEHLT : ZEILE}
    >
      <span className="shrink-0 w-2 pt-2">
        {ungelesen && <span className="block size-2 rounded-full bg-primary" aria-label="ungelesen" role="img" />}
      </span>
      <span className="min-w-0 grow">
        <span className="flex items-center justify-between gap-x-2">
          <KanalBadge kanal={eintrag.kanal} />
          <span className="text-xs text-muted-foreground-1">{zeitKurz(eintrag.erstellt)}</span>
        </span>
        <span className={ungelesen ? BETREFF_UNGELESEN : BETREFF}>{eintrag.betreff}</span>
        <span className="mt-0.5 block truncate text-xs text-muted-foreground-1">{eintrag.vorschau}</span>
      </span>
    </button>
  );
}

function Kopfzeile({ name, wert }: { name: string; wert: string }) {
  return (
    <div className="flex gap-x-4">
      <dt className="w-24 shrink-0 text-muted-foreground-1">{name}</dt>
      <dd className="min-w-0 grow break-words text-foreground">{wert}</dd>
    </div>
  );
}

function DetailAnsicht({ detail, fenster }: { detail: PostfachDetailDaten; fenster: boolean }) {
  return (
    <article className="space-y-4" aria-label={detail.betreff}>
      <div className="flex items-center justify-between gap-x-2">
        <KanalBadge kanal={detail.kanal} />
        <span className="text-xs text-muted-foreground-1">{zeitLang(detail.erstellt)}</span>
      </div>
      <h3 className="text-lg font-semibold text-foreground break-words">{detail.betreff}</h3>
      <dl className="space-y-1.5 rounded-lg bg-muted p-3 text-sm">
        {detail.an.length > 0 && <Kopfzeile name="An" wert={detail.an.join(", ")} />}
        {detail.cc.length > 0 && <Kopfzeile name="Cc" wert={detail.cc.join(", ")} />}
        {detail.bcc.length > 0 && <Kopfzeile name="Bcc" wert={detail.bcc.join(", ")} />}
        {detail.von && <Kopfzeile name="Von" wert={detail.von} />}
        <Kopfzeile name="Ausgelöst" wert={zeitLang(detail.erstellt)} />
        <Kopfzeile name="Vorlage" wert={detail.quelle} />
        <Kopfzeile name="Status" wert={statusLabel(detail.status)} />
        {detail.anhaenge.length > 0 && (
          <Kopfzeile name="Anhänge" wert={detail.anhaenge.map((a) => `${a.name} (${groesseLabel(a.groesse)})`).join(", ")} />
        )}
      </dl>
      {detail.hinweis && <Alert ton="info">{detail.hinweis}</Alert>}
      {detail.geschwaerzt && <Alert ton="neutral">Zugangslinks sind in fremden Postfächern geschwärzt.</Alert>}
      {detail.html !== null ? (
        <div className="rounded-lg border border-layer-line overflow-hidden">
          <p className="px-3 py-1.5 text-xs text-muted-foreground-1 bg-muted border-b border-layer-line">Darstellung der E-Mail (abgeschottet, ohne Skripte und externe Bilder)</p>
          {/* Abgeschottet: sandbox="" ohne allow-scripts und ohne allow-same-origin; CSP im srcdoc blockiert externe Bilder (Tracking-Pixel). */}
          <iframe sandbox="" srcDoc={mailSrcdoc(detail.html)} title="Darstellung der E-Mail" referrerPolicy="no-referrer" className="block w-full h-96 bg-layer" />
        </div>
      ) : (
        <div className="rounded-lg border border-layer-line p-4 text-sm text-foreground whitespace-pre-wrap break-words">{detail.text}</div>
      )}
      <LinkListe html={detail.html} text={detail.text} fenster={fenster} />
    </article>
  );
}

/**
 * „Links in dieser Nachricht“ (W116 b): die Mail-Darstellung bleibt ohne klickbare Links, hier stehen die Ziele mit Linktext und
 * voller Adresse. Portal (gleiche Origin, Portalpfad) im selben Tab, extern in neuem Tab mit Symbol. Die Liste entsteht aus dem,
 * was der Server schon geschwärzt ausgeliefert hat (`detail.html`/`detail.text`), nie aus Rohdaten; ein geschwärzter Zugangslink
 * ist dort nur Platzhaltertext und damit nie ein Ziel. Ohne Links gibt es den Abschnitt nicht.
 */
function LinkListe({ html, text, fenster }: { html: string | null; text: string; fenster: boolean }) {
  const { links, weitere } = extrahiereLinks(html, text, typeof window === "undefined" ? "" : window.location.origin);
  const id = useId();
  // Abgekoppeltes Fenster (W119 a): ein Portal-Link öffnet im Hauptfenster (Opener, gleiche Origin), sonst in einem neuen Tab;
  // das Fenster selbst navigiert nie weg. Im Modal bleibt es der selbe Tab.
  const imHauptfenster = (ziel: string) => (e: React.MouseEvent<HTMLAnchorElement>) => {
    if (oeffneImHauptfenster(ziel, window)) e.preventDefault();
  };
  if (links.length === 0) return null;
  return (
    <section aria-labelledby={`${id}-links`} className="space-y-2">
      <h4 id={`${id}-links`} className="text-sm font-semibold text-foreground">
        Links in dieser Nachricht
      </h4>
      {/* Preline: component/list-group/list-group-links (Zeilen mit Linkknopf `Knopf` text und Adresse darunter; Lucide ExternalLink/Link, R-F; Abweichung AW-083 (12)) */}
      <ul className="divide-y divide-line-2 rounded-lg border border-layer-line" aria-label="Links in dieser Nachricht">
        {links.map((l) => (
          <li key={l.ziel} className="px-3 py-2 min-w-0">
            {l.portal && fenster ? (
              <Knopf variante="text" href={l.ziel} extern onClick={imHauptfenster(l.ziel)} symbol={<LinkSymbol />} ariaLabel={`${l.text} (öffnet im Hauptfenster)`}>
                {l.text}
              </Knopf>
            ) : l.portal ? (
              <Knopf variante="text" href={l.ziel} symbol={<LinkSymbol />}>
                {l.text}
              </Knopf>
            ) : (
              <Knopf variante="text" href={l.ziel} extern symbol={<ExternSymbol />} ariaLabel={`${l.text} (öffnet in neuem Tab)`}>
                {l.text}
              </Knopf>
            )}
            <p className="mt-0.5 text-xs text-muted-foreground-1 break-all">{l.ziel}</p>
          </li>
        ))}
      </ul>
      {weitere > 0 && (
        <p className="text-xs text-muted-foreground-1" role="status">
          Es werden die ersten {links.length} Links gezeigt, {weitere} {weitere === 1 ? "weiterer Link ist" : "weitere Links sind"} ausgeblendet.
        </p>
      )}
    </section>
  );
}
